import { useCallback, useContext, useSyncExternalStore } from 'react';
import { ReactReduxContext } from 'react-redux';
import { userApi } from '@features/user/store/api';
import { selectSubjectLanguageVariants } from '@features/user/store/selectors';

import { DEFAULT_SUBJECT_LANGUAGE, type SubjectLanguageVariants } from '../utils/subjectLanguage';

type SelectorState = Parameters<typeof selectSubjectLanguageVariants>[0];

const selectGuideSubjectLanguage = (state: unknown): SubjectLanguageVariants =>
  typeof state === 'object' && state !== null && userApi.reducerPath in state
    ? selectSubjectLanguageVariants(state as SelectorState)
    : DEFAULT_SUBJECT_LANGUAGE;

const subscribeToNothing = (): (() => void) => () => undefined;

/**
 * The clinic's subject language for link wording: the same variants as
 * `useSubjectLanguageVariants`, read from the user store's selector rather
 * than the `@features/user` barrel, which surface tests replace wholesale.
 * Without a Redux provider, or in a store without the API slice (component
 * tests, stories), it reads as a practice that says "patient".
 */
export function useGuideSubjectLanguage(): SubjectLanguageVariants {
  const redux = useContext(ReactReduxContext);
  const subscribe = useCallback(
    (onChange: () => void) => (redux ? redux.store.subscribe(onChange) : subscribeToNothing()),
    [redux],
  );
  const getSnapshot = useCallback(
    () => (redux ? selectGuideSubjectLanguage(redux.store.getState()) : DEFAULT_SUBJECT_LANGUAGE),
    [redux],
  );
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
